import { LinkButton } from '@euler/app/components/LinkButton';
import { Typography } from '@euler/app/components/typography';
import { OrderDraft } from '@euler/app/flows/order/types';
import { Center } from '@euler/components/Center';
import { StatusColors } from '@euler/components/Colors';
import { Icon } from '@euler/components/Icomoon';
import { Img } from '@euler/components/image/AdvancedImage';
import { HStack } from '@euler/components/layout/Stack';
import { PredefinedHitSlops } from '@euler/functions/predefinedHitSlops';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useGeneralBottomSheetModal } from '@euler/functions/useBottomSheetModal';
import { useSystemMetrics } from '@euler/functions/useSystemMetrics';
import { withConfirmation } from '@euler/utils';
import { formatDateTime } from '@euler/utils/formatters';
import { usePersistFn } from '@euler/utils/hooks';
import { BottomSheetModal, BottomSheetScrollView } from '@gorhom/bottom-sheet';
import { memo, useCallback } from 'react';
import { Platform, ScrollView, TouchableOpacity, View } from 'react-native';
import { useSharedValue } from 'react-native-reanimated';
import { useCheckExistingOrder } from '../functions/useCheckExistingOrder';
import { useOrderDraftModal } from '../functions/useOrderDraftModal';
import { useOrderDrafts } from '../functions/useOrderDrafts';

export const OrderDraftModal = memo(() => {
  const { visible, onDismiss } = useOrderDraftModal();
  const { rpx } = useResponsiveContext();
  const { navBarHeight, safeAreaInsets: insets } = useSystemMetrics();
  const { draftManager, availableDrafts, loadDraft, clearDrafts } =
    useOrderDrafts();

  const { bottomSheetModalRef, ...bottomSheetProps } =
    useGeneralBottomSheetModal({
      show: visible,
      topMargin: navBarHeight,
      minSnapPoint: '50%',
      mediumSnapPoint: '75%',
      index: 1,
      enableDismissOnClose: true,
      enablePanDownToClose: true,
      dismissWhenPressBackdrop: true,
      onDismiss,
    });
  const animatedPosition = useSharedValue(0);

  const checkExistingOrder = useCheckExistingOrder();

  const onPress = usePersistFn(async (draft: OrderDraft) => {
    if (
      await withConfirmation({
        title: '提示',
        message: '是否确认加载该草稿内容到当前页面? ',
      })
    ) {
      loadDraft(draft);
      if (draft.form.licensePlateNo) {
        void checkExistingOrder('plate', draft.form.licensePlateNo);
      } else if (draft.form.vin) {
        void checkExistingOrder('vin', draft.form.vin);
      }
    }
  });

  const onClear = usePersistFn(async () => {
    if (
      await withConfirmation({
        title: '提示',
        message: '是否确认清空所有建单草稿? ',
      })
    ) {
      clearDrafts();
    }
  });

  const onRemove = usePersistFn(async (draft: OrderDraft) => {
    draftManager.removeDraft(draft.id);
  });

  const Header = useCallback(
    () => (
      <Center
        css={`
          border-bottom-width: 1px;
          border-bottom-color: #f1f1f1;
          margin-top: ${rpx(-4)}px;
          margin-bottom: ${rpx(4)}px;
          height: ${rpx(44)}px;
        `}
      >
        <Typography.Label size={16} color="#333" bold>
          建单草稿记录
        </Typography.Label>
        <Center
          css={`
            position: absolute;
            right: ${rpx(15)}px;
            top: 0;
            bottom: 0;
          `}
        >
          <LinkButton text="清空" onPress={onClear} />
        </Center>
      </Center>
    ),
    [onClear, rpx],
  );

  // Fix: BottomSheet specific scroll view components do not work on Web right now.
  // https://github.com/gorhom/react-native-bottom-sheet/issues/11
  const ScrollComponent =
    Platform.OS === 'web' ? ScrollView : BottomSheetScrollView;

  return (
    <BottomSheetModal
      {...bottomSheetProps}
      animatedPosition={animatedPosition}
      keyboardBehavior="interactive"
      keyboardBlurBehavior="none"
      ref={bottomSheetModalRef}
    >
      <ScrollComponent
        showsVerticalScrollIndicator={Platform.OS === 'web'}
        contentContainerStyle={{
          paddingBottom: insets.bottom + rpx(15),
        }}
      >
        <Header />
        {availableDrafts.map(draft => (
          <Cell
            key={draft.id}
            draft={draft}
            onPress={onPress}
            onRemove={onRemove}
          />
        ))}
      </ScrollComponent>
    </BottomSheetModal>
  );
});

const Cell = memo(
  ({
    draft,
    onPress,
    onRemove,
  }: {
    draft: OrderDraft;
    onPress?: (draft: OrderDraft) => void;
    onRemove?: (draft: OrderDraft) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const handlePress = useCallback(() => {
      onPress?.(draft);
    }, [onPress, draft]);

    const handleRemove = useCallback(() => {
      onRemove?.(draft);
    }, [onRemove, draft]);

    return (
      <TouchableOpacity
        onPress={handlePress}
        css={`
          flex-direction: row;
          justify-content: space-between;
          align-items: stretch;
          padding: ${rpx(8)}px ${rpx(15)}px;
          min-height: ${rpx(36)}px;
          border-bottom-width: 1px;
          border-bottom-color: #ddd;
        `}
      >
        <View
          css={`
            flex: 1;
          `}
        >
          <HStack justifyContent="flex-start">
            <Typography.Label
              medium
              size={rpx(20)}
              color={'#333'}
              lineHeight={rpx(26)}
            >
              {draft.form.licensePlateNo || '无车牌'}
            </Typography.Label>
            {draft.form.vehicleInfo?.vehicleBrandLogo ? (
              <Img
                uri={draft.form.vehicleInfo?.vehicleBrandLogo}
                css={`
                  width: ${rpx(24)}px;
                  height: ${rpx(24)}px;
                  margin-left: ${rpx(4)}px;
                `}
              />
            ) : null}
          </HStack>
          {draft.form.vehicleInfo?.vehicleName ? (
            <Typography.Label color="#555" regular size={rpx(16)}>
              {draft.form.vehicleInfo?.vehicleName}
            </Typography.Label>
          ) : null}
          <Typography.Label light color="#777" size={rpx(12)}>
            保存时间: {formatDateTime(draft.timestamp ?? Date.now())}
          </Typography.Label>
        </View>
        <TouchableOpacity
          onPress={handleRemove}
          hitSlop={PredefinedHitSlops.large}
          css={`
            justify-content: center;
          `}
        >
          <Icon
            name="minus-circle"
            size={rpx(20)}
            color={StatusColors.Danger}
          />
        </TouchableOpacity>
      </TouchableOpacity>
    );
  },
);
